// North County beaches added October 2026 (Oceanside → Del Mar).
// Full profiles including extended modules, merged into BEACH_PROFILES_SEED,
// BEACH_IMAGES and BEACH_AMENITIES in beachProfiles.ts so every listing,
// search, sitemap and site-index source picks them up automatically.
// NOTE: scripts/lib/route-sources.mjs reads `id: "…"` from this file — keep
// `id` keys reserved for beach slugs only.

import type { BeachProfileData, BeachAmenities } from "./beachProfiles";
import oceansideHarborImg from "@/assets/beach-oceanside-harbor.jpg";
import breakwaterWayImg from "@/assets/beach-breakwater-way.jpg";
import tysonStreetImg from "@/assets/beach-tyson-street.jpg";
import southCarlsbadImg from "@/assets/beach-south-carlsbad.jpg";
import northPontoImg from "@/assets/beach-north-ponto.jpg";
import southPontoImg from "@/assets/beach-south-ponto.jpg";
import terramarImg from "@/assets/beach-terramar.jpg";
import warmWaterJettyImg from "@/assets/beach-warm-water-jetty.jpg";
import beaconsImg from "@/assets/beach-beacons.jpg";
import grandviewImg from "@/assets/beach-grandview.jpg";
import stoneStepsImg from "@/assets/beach-stone-steps.jpg";
import tideParkImg from "@/assets/beach-tide-park.jpg";
import delMarShoresImg from "@/assets/beach-del-mar-shores.jpg";
import delMarDogBeachImg from "@/assets/beach-del-mar-dog-beach.jpg";

export const NORTH_COUNTY_IMAGES: Record<string, string> = {
  "oceanside-harbor": oceansideHarborImg,
  "breakwater-way": breakwaterWayImg,
  "tyson-street": tysonStreetImg,
  "south-carlsbad": southCarlsbadImg,
  "north-ponto": northPontoImg,
  "south-ponto": southPontoImg,
  terramar: terramarImg,
  "warm-water-jetty": warmWaterJettyImg,
  beacons: beaconsImg,
  grandview: grandviewImg,
  "stone-steps": stoneStepsImg,
  "tide-park": tideParkImg,
  "del-mar-shores": delMarShoresImg,
  "del-mar-dog-beach": delMarDogBeachImg,
};

// La Jolla is NOAA's tide reference for the North County coast.
const NORTH_COUNTY_TIDES = "9410230";
const WATER = [0, 0, 0, 1, 1, 2, 2, 3, 3, 2, 1, 0];
const SUN_NORTH = [1, 1, 1, 2, 1, 0, 2, 3, 3, 2, 2, 1];
const SURF_WINTER = [3, 3, 2, 2, 2, 2, 2, 2, 2, 3, 3, 3];
const SURF_SOUTH = [2, 2, 2, 2, 3, 3, 3, 3, 3, 2, 2, 2];
const CROWD_BUSY = [1, 1, 1, 2, 2, 3, 3, 3, 2, 1, 1, 1];
const CROWD_QUIET = [0, 0, 1, 1, 1, 2, 2, 2, 1, 1, 0, 0];
const WATER_TEMP_FACT = { icon: "Thermometer" as const, label: "Water temp", value: "57°F (Feb) – 70°F (Sept)" };

const maps = (q: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const LEUCADIA_SAFETY_HAZARDS = [
  "Unstable sandstone bluffs — never sit or lie directly below the cliff face",
  "Narrow beach can disappear at high tide; check the tide before walking far",
  "Cobbles and reef underfoot — water shoes help",
];

type NC = BeachProfileData & { amenities: BeachAmenities };

export const NORTH_COUNTY_BEACHES: NC[] = [
  // ─── Oceanside ────────────────────────────────────────────
  {
    id: "oceanside-harbor-beach",
    name: "Oceanside Harbor Beach",
    location: "Oceanside",
    imageKey: "oceanside-harbor",
    maps: maps("Oceanside Harbor Beach, Oceanside, CA"),
    coords: { lat: 33.2033, lng: -117.3926 },
    paragraphs: [
      "Tucked between the north jetty of Oceanside Harbor and the pier-side neighborhoods to the south, Harbor Beach is Oceanside's most complete beach day: a wide, sandy shore with fire rings, volleyball, restrooms and showers, all a short walk from the harbor's Cape Cod–style shops, fish markets and charter boats.",
      "The jetty knocks down a lot of the wind and swell, so the shorebreak here is often friendlier than at the pier, and families spread out along the sand with umbrellas and coolers. Surfers work the sandbars along the jetty when a northwest swell lines up, and bodyboarders love the wedging peaks on bigger days.",
      "Parking is in the paid harbor lots right behind the sand — rates and time limits are posted at the pay stations and change seasonally. On summer weekends the closest lots fill by late morning, so arrive early or plan an evening bonfire visit when day-trippers head home.",
      "Pair a beach session with a stroll around the harbor village: watch fishing boats come in, grab fish and chips, or rent a kayak or paddleboard inside the calm harbor basin.",
    ],
    insiderTip: "Fire rings are first-come, first-served — claim one in the late afternoon and bring your own firewood (no pallets). Sunsets behind the jetty are spectacular.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "Paid harbor lots behind the beach" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Yes — Oceanside lifeguards, harbor HQ nearby" },
      { icon: "Bath", label: "RESTROOMS", value: "Yes, plus outdoor showers" },
      { icon: "Flame", label: "Fire rings", value: "Yes, first-come" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "moderate", restrooms: "yes", lifeguard: "yes", dogs: "no", accessibility: "partial",
      notes: { parking: "Paid harbor lots; fill on summer weekends", restrooms: "Restrooms, showers, fire rings", dogs: "No dogs on Oceanside beaches", accessibility: "Paved paths to the sand edge" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    gettingThere: {
      driving: "From I-5 take Harbor Drive west into Oceanside Harbor, then follow signs to Harbor Beach along North Harbor Drive.",
      parking: "Paid lots directly behind the sand; pay at the kiosks or app. Overflow parking is spread around the harbor.",
      transit: "Oceanside Transit Center (COASTER, SPRINTER, Amtrak) is about 1.5 miles south — bike or walk the Strand.",
      tips: ["Arrive before 10 AM on summer weekends", "Bring quarters/app for meters", "Harbor shops have restrooms too"],
    },
    safety: {
      rip: "Rips form along the jetty and between sandbars — swim near an open lifeguard tower.",
      hazards: ["Don't climb on the jetty rocks — slippery and wave-washed", "Boat traffic at the harbor mouth", "Strong shorebreak on bigger swells"],
      waterQuality: "Check the county's beach water-quality status after rain; harbor runoff can cause advisories.",
      lifeguard: "City of Oceanside lifeguards patrol year-round, with more towers staffed in summer.",
    },
    activities: [
      { kind: "swim", rating: "great", note: "Jetty-protected" },
      { kind: "bonfire", rating: "great", note: "Fire rings" },
      { kind: "volleyball", rating: "good" },
      { kind: "surf", rating: "good", note: "Jetty sandbars" },
      { kind: "kayak", rating: "great", note: "Inside the harbor" },
      { kind: "fish", rating: "good", note: "Harbor charters" },
      { kind: "dogs", rating: "avoid", note: "Not on the beach" },
    ],
    packing: {
      bring: ["Firewood for the fire rings", "Beach chairs and umbrella", "Cooler (no glass)", "Payment app for parking"],
      rentNearby: ["Kayaks and paddleboards in the harbor", "Sportfishing and whale-watching charters"],
      skip: ["Glass bottles", "Dogs on the sand"],
    },
    photoGuide: {
      bestTime: "Sunset — the sun drops behind the jetty and harbor mouth.",
      bestSpots: ["Shoreline looking north to the jetty", "Harbor village boardwalk", "Fire rings at blue hour"],
      tips: ["Include a fire ring glow in foreground for dusk shots.", "Telephoto for boats rounding the jetty."],
    },
    seasonalCalendar: {
      summary: "A summer favorite for bonfires and family days; quieter, clearer and better for surf in fall and winter.",
      rows: { crowd: CROWD_BUSY, water: WATER, surf: SURF_WINTER, weather: SUN_NORTH },
      events: [{ months: "Dec–Apr", label: "Gray whale charters from the harbor" }],
    },
    bestFor: [
      { label: "Bonfires", icon: "Flame" },
      { label: "Families", icon: "Users" },
      { label: "Harbor strolls", icon: "Camera" },
    ],
    parkingSnapshot: { cost: "Paid harbor lots", bestLot: "North Harbor Drive beach lots", backupLot: "Harbor village lots", fillsBy: "10–11 AM summer weekends" },
    dining: [
      { slug: "hello-betty-fish-house", reason: "Fish tacos and cocktails near the pier", distance: "5-min drive" },
      { slug: "beach-break-cafe", reason: "Big surf-town breakfasts", distance: "5-min drive" },
      { slug: "petite-madeline-bakery", reason: "Pastries and coffee before the beach", distance: "6-min drive" },
    ],
    nearbyAttractions: [
      { to: "/beaches/oceanside-pier", label: "Oceanside Pier", hint: "Walk the West Coast's longest wooden pier." },
      { to: "/beaches/breakwater-way-beach", label: "Breakwater Way Beach", hint: "Quieter sand on the north side of the harbor." },
      { to: "/tides", label: "Tide chart", hint: "Plan around La Jolla tide predictions." },
    ],
  },
  {
    id: "breakwater-way-beach",
    name: "Breakwater Way Beach",
    location: "Oceanside",
    imageKey: "breakwater-way",
    maps: maps("Breakwater Way, Oceanside, CA"),
    coords: { lat: 33.2085, lng: -117.3965 },
    paragraphs: [
      "Breakwater Way runs along the north side of Oceanside Harbor, and the stretch of sand beside it is one of the calmest, least-crowded beaches in the city. Long rock breakwaters shelter the shoreline, so on most days the water is glassy and gentle — a favorite for early walks, paddleboard launches and quiet afternoons with a book.",
      "It's not a big-wave beach or a party beach. Instead you get wide views across the harbor mouth, pelicans diving off the rocks, and the soft bustle of boats heading out to sea. Kids like poking around the calmer water near the sand, though there's no lifeguard tower right on this stretch most of the year.",
      "Parking is in the paid harbor lots along Breakwater Way and North Harbor Drive. Restrooms are in the harbor area, a short walk away.",
    ],
    insiderTip: "Come at sunrise for glassy water and launch a paddleboard straight off the sand — rentals are a few minutes away inside the harbor.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "Paid harbor lots" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Limited — nearest towers at Harbor Beach" },
      { icon: "Bath", label: "RESTROOMS", value: "In the harbor, short walk" },
      { icon: "Heart", label: "Best for", value: "Calm water, paddleboarding, quiet walks" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "moderate", restrooms: "limited", lifeguard: "limited", dogs: "no", accessibility: "partial",
      notes: { parking: "Paid harbor lots", restrooms: "Harbor restrooms nearby", lifeguard: "Patrol only; towers at Harbor Beach", dogs: "No dogs on the sand" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    gettingThere: {
      driving: "From I-5 take Harbor Drive west, then follow North Harbor Drive to Breakwater Way.",
      parking: "Paid harbor parking; pay stations along the road.",
      transit: "About 2 miles from the Oceanside Transit Center — bike via the harbor.",
      tips: ["Weekday mornings are nearly empty", "Combine with a harbor kayak rental"],
    },
    safety: {
      rip: "Usually mild thanks to the breakwaters, but currents run strong near the harbor mouth.",
      hazards: ["Stay off the breakwater rocks", "Boat traffic in the channel", "No regular lifeguard tower on this stretch"],
      waterQuality: "Harbor water can be affected by runoff — check advisories after rain.",
      lifeguard: "Oceanside lifeguards patrol by vehicle; staffed towers are at Harbor Beach.",
    },
    activities: [
      { kind: "sup", rating: "great", note: "Glassy mornings" },
      { kind: "kayak", rating: "great" },
      { kind: "swim", rating: "good", note: "Calm but unguarded" },
      { kind: "photo", rating: "good", note: "Harbor and pelicans" },
      { kind: "surf", rating: "avoid", note: "Sheltered" },
      { kind: "dogs", rating: "avoid" },
    ],
    packing: { bring: ["Paddleboard or kayak", "Binoculars for birds", "Snacks and water"], rentNearby: ["SUP and kayak rentals in the harbor"], skip: ["Surfboard — sheltered water"] },
    photoGuide: { bestTime: "Sunrise for glassy reflections; sunset for silhouettes of the breakwater.", bestSpots: ["Shoreline facing the harbor mouth", "Breakwater with the green channel marker"], tips: ["Brown pelicans dive close to shore in the morning."] },
    seasonalCalendar: { summary: "Calm and quiet year-round; the best paddling days are late summer and fall.", rows: { crowd: CROWD_QUIET, water: WATER, weather: SUN_NORTH } },
    bestFor: [{ label: "Paddleboarding", icon: "Waves" }, { label: "Quiet walks", icon: "Heart" }, { label: "Birdwatching", icon: "Camera" }],
    parkingSnapshot: { cost: "Paid harbor parking", bestLot: "Breakwater Way lots", backupLot: "North Harbor Drive lots", fillsBy: "Rarely full, except summer weekends" },
    dining: [
      { slug: "hello-betty-fish-house", reason: "Seafood and cocktails", distance: "6-min drive" },
      { slug: "that-boy-good-bbq", reason: "Smoky post-beach BBQ", distance: "6-min drive" },
    ],
    nearbyAttractions: [
      { to: "/beaches/oceanside-harbor-beach", label: "Oceanside Harbor Beach", hint: "Fire rings and lifeguards next door." },
      { to: "/beaches/oceanside-pier", label: "Oceanside Pier", hint: "Classic pier and surf town." },
    ],
  },
  {
    id: "tyson-street-beach",
    name: "Tyson Street Beach",
    location: "Oceanside",
    imageKey: "tyson-street",
    maps: maps("Tyson Street Park, Oceanside, CA"),
    coords: { lat: 33.1905, lng: -117.3773 },
    paragraphs: [
      "Just south of the Oceanside Pier, Tyson Street Beach is where downtown Oceanside goes to play: volleyball nets, a small grassy park, picnic tables, and a wide sweep of sand with the pier framing the view.",
      "The Strand walkway runs right past, so it's easy to stroll to the pier, the Junior Seau Beach Community Center, or the restaurants on Coast Highway. Surfers paddle out on the sandbars south of the pier, and lifeguards staff the towers in summer.",
      "Parking is metered along the Strand and nearby streets, with paid lots closer to the pier. It's a walkable beach from the Oceanside Transit Center, making it one of the easiest car-free beach days in North County.",
    ],
    insiderTip: "Grab a picnic table at Tyson Street Park in the morning, then walk the Strand to the pier at sunset — it's under ten minutes.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "Metered street + pier-area lots" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Yes, summer towers" },
      { icon: "Bath", label: "RESTROOMS", value: "Yes, near the pier" },
      { icon: "Users", label: "Best for", value: "Volleyball, picnics, pier walks" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "moderate", restrooms: "yes", lifeguard: "yes", dogs: "no", accessibility: "full",
      notes: { parking: "Metered street, paid pier lots", dogs: "No dogs on the sand", accessibility: "Flat Strand walkway beside the park" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    gettingThere: {
      driving: "From I-5 take Mission Avenue west, then turn south on the Strand or Pacific Street to Tyson Street.",
      parking: "Metered spaces along the Strand and Pacific Street; paid lots by the pier.",
      transit: "About a 10-minute walk from the Oceanside Transit Center (COASTER, Amtrak, SPRINTER).",
      tips: ["Take the train and skip parking entirely", "Sunday farmers market nearby (Thursdays downtown)"],
    },
    safety: {
      rip: "Rips form beside the pier pilings and between sandbars.",
      hazards: ["Stay well clear of pier pilings when surfing or swimming", "Shorebreak on bigger swells"],
      waterQuality: "Generally good; check advisories after rain.",
      lifeguard: "Oceanside lifeguards staff towers in summer and patrol year-round.",
    },
    activities: [
      { kind: "volleyball", rating: "great" },
      { kind: "swim", rating: "good" },
      { kind: "surf", rating: "good", note: "South of the pier" },
      { kind: "picnic", rating: "great", note: "Tyson Street Park" },
      { kind: "bike", rating: "good", note: "The Strand" },
      { kind: "dogs", rating: "avoid" },
    ],
    packing: { bring: ["Volleyball", "Picnic lunch", "Sunscreen"], rentNearby: ["Surfboards near the pier", "Bikes downtown"], skip: ["Glass", "Dogs on the sand"] },
    photoGuide: { bestTime: "Golden hour with the pier silhouetted to the north.", bestSpots: ["Park bluff looking at the pier", "Waterline at low tide for reflections"], tips: ["Volleyball action shots at sunset."] },
    seasonalCalendar: { summary: "Lively all summer; mellow and sunny in fall.", rows: { crowd: CROWD_BUSY, water: WATER, surf: SURF_WINTER, weather: SUN_NORTH }, events: [{ months: "Aug", label: "World Bodysurfing Championships at the pier" }] },
    bestFor: [{ label: "Volleyball", icon: "Users" }, { label: "Car-free day", icon: "Bike" }, { label: "Sunset walks", icon: "Sun" }],
    parkingSnapshot: { cost: "Metered street / paid lots", bestLot: "Strand metered spaces", backupLot: "Pier-area paid lots", fillsBy: "11 AM summer weekends" },
    dining: [
      { slug: "beach-break-cafe", reason: "Breakfast steps from the Strand", distance: "5-min walk" },
      { slug: "petite-madeline-bakery", reason: "Croissants and coffee", distance: "8-min walk" },
      { slug: "hello-betty-fish-house", reason: "Rooftop seafood near the pier", distance: "8-min walk" },
    ],
    nearbyAttractions: [
      { to: "/beaches/oceanside-pier", label: "Oceanside Pier", hint: "Right next door." },
      { to: "/guides/things-to-do-in-oceanside", label: "Things to do in Oceanside", hint: "Plan a full day downtown." },
    ],
  },

  // ─── Carlsbad ─────────────────────────────────────────────
  {
    id: "south-carlsbad-state-beach",
    name: "South Carlsbad State Beach",
    location: "Carlsbad",
    imageKey: "south-carlsbad",
    maps: maps("South Carlsbad State Beach, Carlsbad, CA"),
    coords: { lat: 33.1007, lng: -117.3172 },
    paragraphs: [
      "South Carlsbad State Beach stretches for miles below a low bluff lined with one of California's most sought-after oceanfront campgrounds. Stairways drop from the campground to a long, uncrowded beach of sand and cobbles with consistent surf and huge sunsets.",
      "Day visitors can use the state day-use parking (fee) or the free pullouts along Carlsbad Boulevard, then walk down the stairs. The beach stays quiet even in summer because it's long enough to swallow the crowds, and campers have their own private-feeling stretch below their sites.",
      "Campsites book up months in advance through ReserveCalifornia. Lifeguards are State Parks seasonal staff, and restrooms and showers are in the campground area.",
    ],
    insiderTip: "Book campsites exactly when the reservation window opens (six months ahead) — bluff-edge sites go in minutes.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "State day-use fee or free highway pullouts" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Seasonal, State Parks" },
      { icon: "Bath", label: "RESTROOMS", value: "Yes, in campground area" },
      { icon: "Flame", label: "Best for", value: "Beach camping, surf, sunsets" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "moderate", restrooms: "yes", lifeguard: "seasonal", dogs: "no", accessibility: "partial",
      notes: { parking: "Day-use fee; free pullouts on Carlsbad Blvd", restrooms: "Campground restrooms and showers", lifeguard: "State Parks lifeguards in season", dogs: "Dogs allowed in campground only, not on beach", accessibility: "Stairs to sand; bluff-top paths" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    gettingThere: {
      driving: "From I-5 exit Poinsettia Lane west, then go south on Carlsbad Boulevard to the state beach entrance.",
      parking: "State day-use parking (fee) at the entrance; free pullouts along Carlsbad Blvd fill on weekends.",
      transit: "Poinsettia COASTER station is about a mile inland.",
      tips: ["Camping reservations via ReserveCalifornia", "Bring water shoes for the cobbles"],
    },
    safety: {
      rip: "Moderate rips between sandbars; swim near staffed towers.",
      hazards: ["Steep stairs from the bluff", "Cobble shelves at high tide", "Bluff erosion — stay off the cliff edge"],
      waterQuality: "Usually good; check advisories after rain.",
      lifeguard: "California State Parks lifeguards staff towers seasonally.",
    },
    activities: [
      { kind: "surf", rating: "great", note: "Consistent beachbreak" },
      { kind: "swim", rating: "good" },
      { kind: "sunset", rating: "great" },
      { kind: "picnic", rating: "good", note: "Campground" },
      { kind: "bike", rating: "good", note: "Coast Hwy bike lane" },
      { kind: "dogs", rating: "avoid", note: "Campground only" },
    ],
    packing: { bring: ["Camping gear (with reservation)", "Water shoes", "Wetsuit", "Headlamp"], rentNearby: ["Surfboards in Carlsbad Village", "Supplies at Poinsettia shopping center"], skip: ["Dogs on the sand"] },
    photoGuide: { bestTime: "Sunset from the bluff-top campground paths.", bestSpots: ["Top of the campground stairs", "Cobble shelf at low tide"], tips: ["Long exposures over the cobbles at dusk."] },
    seasonalCalendar: { summary: "Camping peaks in summer; fall brings warm water, offshore winds and empty peaks.", rows: { crowd: CROWD_BUSY, water: WATER, surf: SURF_WINTER, weather: SUN_NORTH } },
    bestFor: [{ label: "Beach camping", icon: "Flame" }, { label: "Surfing", icon: "Waves" }, { label: "Sunsets", icon: "Sun" }],
    parkingSnapshot: { cost: "State day-use fee", bestLot: "State beach day-use lot", backupLot: "Carlsbad Blvd pullouts", fillsBy: "9–10 AM summer weekends" },
    dining: [
      { slug: "pollos-maria", reason: "Grilled chicken for a campsite dinner", distance: "8-min drive" },
      { slug: "senor-grubby-s", reason: "Burritos after a surf", distance: "10-min drive" },
    ],
    nearbyAttractions: [
      { to: "/guides/beach-camping-san-diego", label: "Beach camping guide", hint: "Every San Diego beach campground compared." },
      { to: "/beaches/north-ponto-beach", label: "North Ponto Beach", hint: "Day-use beach just south." },
      { to: "/guides/legoland-california", label: "LEGOLAND California", hint: "Ten minutes inland." },
    ],
  },
  {
    id: "north-ponto-beach",
    name: "North Ponto Beach",
    location: "Carlsbad",
    imageKey: "north-ponto",
    maps: maps("Ponto Beach, Carlsbad, CA"),
    coords: { lat: 33.0885, lng: -117.3115 },
    paragraphs: [
      "North Ponto is the day-use heart of South Carlsbad State Beach — a broad, flat stretch of sand with a big parking lot right behind it, restrooms, showers and a mellow vibe that makes it one of North County's easiest beach days.",
      "Surfers spread out along the sandbars, families set up camp close to the lot, and the sand is wide enough that even busy summer days feel roomy. It's flat enough that a beach wagon rolls straight from the car to the water.",
      "The state day-use lot charges a fee; free parking along Carlsbad Boulevard is available but fills quickly on weekends. Lifeguards are State Parks seasonal staff.",
    ],
    insiderTip: "Skip the lot fee by parking along Carlsbad Boulevard north of the entrance before 9 AM, then walk in along the beach.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "State lot (fee) right behind the sand" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Seasonal, State Parks" },
      { icon: "Bath", label: "RESTROOMS", value: "Yes, plus showers" },
      { icon: "Users", label: "Best for", value: "Easy family days, beginner surf" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "easy", restrooms: "yes", lifeguard: "seasonal", dogs: "no", accessibility: "full",
      notes: { parking: "Large state lot, day-use fee", lifeguard: "State Parks, in season", dogs: "No dogs on the beach", accessibility: "Flat walk from lot to sand" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    gettingThere: {
      driving: "From I-5 exit Poinsettia Lane or La Costa Avenue west to Carlsbad Boulevard; the Ponto entrance is near Avenida Encinas.",
      parking: "State day-use lot (fee). Free street parking along Carlsbad Blvd when available.",
      transit: "Poinsettia COASTER station is about 1.5 miles away.",
      tips: ["The lot is the easiest in North County for wagons and strollers", "Bring cash/card for the fee kiosk"],
    },
    safety: {
      rip: "Rips form between the sandbars; swim near staffed towers.",
      hazards: ["Crowded surf lineups on good swells", "Strong sun — little shade"],
      waterQuality: "Check advisories after rain; Batiquitos Lagoon outflow is just south.",
      lifeguard: "California State Parks seasonal lifeguards.",
    },
    activities: [
      { kind: "swim", rating: "great" },
      { kind: "surf", rating: "great", note: "Beginner-friendly sandbars" },
      { kind: "picnic", rating: "good" },
      { kind: "sunset", rating: "great" },
      { kind: "volleyball", rating: "good", note: "Bring your own net" },
      { kind: "dogs", rating: "avoid" },
    ],
    packing: { bring: ["Beach wagon", "Umbrella for shade", "Soft-top surfboard"], rentNearby: ["Surf lessons operate at Ponto in summer"], skip: ["Dogs", "Glass"] },
    photoGuide: { bestTime: "Sunset — wide open horizon.", bestSpots: ["Waterline at low tide", "Lot edge looking north up the coast"], tips: ["Reflections on wet sand at low tide."] },
    seasonalCalendar: { summary: "A summer staple with easy logistics; fall offers the best surf and fewer people.", rows: { crowd: CROWD_BUSY, water: WATER, surf: SURF_WINTER, weather: SUN_NORTH } },
    bestFor: [{ label: "Families", icon: "Users" }, { label: "Beginner surf", icon: "Waves" }, { label: "Easy parking", icon: "Car" }],
    parkingSnapshot: { cost: "State day-use fee", bestLot: "Ponto day-use lot", backupLot: "Carlsbad Blvd shoulder", fillsBy: "10 AM summer weekends" },
    dining: [
      { slug: "pollos-maria", reason: "Takeout chicken for the sand", distance: "8-min drive" },
      { slug: "fish-101", reason: "Fresh seafood in Leucadia", distance: "8-min drive" },
    ],
    nearbyAttractions: [
      { to: "/beaches/south-ponto-beach", label: "South Ponto Beach", hint: "Jetty surf at the lagoon mouth." },
      { to: "/beaches/south-carlsbad-state-beach", label: "South Carlsbad State Beach", hint: "Campground beach just north." },
    ],
  },
  {
    id: "south-ponto-beach",
    name: "South Ponto Beach",
    location: "Carlsbad",
    imageKey: "south-ponto",
    maps: maps("South Ponto Beach, Batiquitos Lagoon, Carlsbad, CA"),
    coords: { lat: 33.0815, lng: -117.3080 },
    paragraphs: [
      "South Ponto sits at the very southern tip of Carlsbad, where Batiquitos Lagoon meets the Pacific through a rock-lined inlet. The jetty shapes some of the best sandbars in the area, and surfers flock here when a winter northwest swell lights it up.",
      "On the beach side it's quieter than North Ponto, with a smaller lot and a wilder feel — lagoon birds, open horizons and a sweeping view south toward Leucadia. Walk inland and you can pick up the Batiquitos Lagoon trail for a change of scenery.",
      "Parking is in the south state day-use lot (fee) or along Carlsbad Boulevard. Restrooms are at the lot; lifeguards are seasonal.",
    ],
    insiderTip: "Check the swell direction — South Ponto works best on a west or northwest swell with light morning winds.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "South state lot (fee) + roadside" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Seasonal, State Parks" },
      { icon: "Bath", label: "RESTROOMS", value: "Yes, at the lot" },
      { icon: "Waves", label: "Best for", value: "Jetty surf, lagoon birding" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "moderate", restrooms: "yes", lifeguard: "seasonal", dogs: "no", accessibility: "partial",
      notes: { parking: "Smaller state lot, day-use fee", lifeguard: "State Parks, in season", dogs: "No dogs on the beach" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    gettingThere: {
      driving: "From I-5 exit La Costa Avenue west to Carlsbad Boulevard, then turn north to the South Ponto lot.",
      parking: "State day-use lot (fee) and limited roadside parking.",
      transit: "Encinitas COASTER station is about 3 miles south.",
      tips: ["Combine with the Batiquitos Lagoon trail", "Mornings have the lightest wind"],
    },
    safety: {
      rip: "Strong currents near the lagoon inlet and jetty — swim away from the mouth.",
      hazards: ["Lagoon outflow currents", "Crowded, competitive lineup on good days", "Slippery jetty rocks"],
      waterQuality: "Lagoon outflow can raise bacteria levels after rain — check advisories.",
      lifeguard: "State Parks lifeguards in season.",
    },
    activities: [
      { kind: "surf", rating: "great", note: "Jetty sandbars" },
      { kind: "swim", rating: "fair", note: "Avoid the inlet" },
      { kind: "photo", rating: "great", note: "Lagoon + jetty sunsets" },
      { kind: "sunset", rating: "great" },
      { kind: "dogs", rating: "avoid" },
    ],
    packing: { bring: ["Wetsuit", "Binoculars for lagoon birds", "Sunscreen"], rentNearby: ["Boards in Leucadia and Carlsbad"], skip: ["Inflatables near the inlet"] },
    photoGuide: { bestTime: "Sunset over the jetty.", bestSpots: ["Lagoon inlet bridge area", "Jetty from the beach"], tips: ["Surfers silhouetted by the sun at the jetty."] },
    seasonalCalendar: { summary: "Best surf in fall and winter; summer is mellower and great for families.", rows: { crowd: CROWD_BUSY, water: WATER, surf: SURF_WINTER, weather: SUN_NORTH, wildlife: [2, 2, 2, 2, 1, 1, 1, 1, 2, 2, 2, 2] } },
    bestFor: [{ label: "Surfing", icon: "Waves" }, { label: "Birding", icon: "Camera" }, { label: "Sunsets", icon: "Sun" }],
    parkingSnapshot: { cost: "State day-use fee", bestLot: "South Ponto lot", backupLot: "Carlsbad Blvd shoulder", fillsBy: "9 AM on good swell days" },
    dining: [
      { slug: "fish-101", reason: "Grilled fish in Leucadia", distance: "6-min drive" },
      { slug: "juanita-s-taco-shop", reason: "Classic post-surf burritos", distance: "8-min drive" },
    ],
    nearbyAttractions: [
      { to: "/guides/coastal-lagoons", label: "Coastal lagoons", hint: "Batiquitos Lagoon trail and wildlife." },
      { to: "/beaches/north-ponto-beach", label: "North Ponto Beach", hint: "Bigger lot and family beach." },
      { to: "/surf", label: "Surf report", hint: "Check today's swell." },
    ],
  },
  {
    id: "terramar-beach",
    name: "Terramar Beach",
    location: "Carlsbad",
    imageKey: "terramar",
    maps: maps("Terramar Beach, Carlsbad, CA"),
    coords: { lat: 33.1290, lng: -117.3370 },
    paragraphs: [
      "Terramar is a local's beach in a residential Carlsbad neighborhood, where a rocky reef point breaks up the long sandy coast. At low tide the reef drains to reveal tide pools full of anemones, sea hares and small crabs; at mid tide surfers ride the reef's rights and lefts.",
      "There are no facilities — no restrooms, no towers, no snack bar — which keeps the crowds down. Access is via the beach stairs and paths off Carlsbad Boulevard and Cerezo Drive.",
      "Free parking is available on the Carlsbad Boulevard shoulder and nearby streets; respect residents' driveways. It's a short drive to restrooms and food at Tamarack or Carlsbad Village.",
    ],
    insiderTip: "Visit on a minus low tide for the best pools — then grab breakfast in the Village afterward.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "Free roadside on Carlsbad Blvd" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "No dedicated tower" },
      { icon: "Bath", label: "RESTROOMS", value: "None" },
      { icon: "Fish", label: "Best for", value: "Tide pools, reef surf" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "moderate", restrooms: "no", lifeguard: "no", dogs: "no", accessibility: "none",
      notes: { parking: "Free roadside, limited", restrooms: "Nearest at Tamarack", lifeguard: "No tower; state patrol", dogs: "No dogs on state beach", accessibility: "Stairs/steep path to sand" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    tidePool: { spot: "Terramar reef point", note: "Reef drains at low tide; best on minus tides in winter and spring afternoons.", maxHeight: 0.5, link: { to: "/guides/tide-pooling-calendar", label: "Tide-pooling calendar" } },
    gettingThere: {
      driving: "From I-5 exit Cannon Road west, then go south on Carlsbad Boulevard to Cerezo Drive.",
      parking: "Free shoulder parking on Carlsbad Blvd; quiet residential streets nearby.",
      transit: "Carlsbad Village COASTER station is about 2.5 miles north.",
      tips: ["No services — bring water and snacks", "Watch your footing on the reef"],
    },
    safety: {
      rip: "Currents run off the reef point; unguarded beach.",
      hazards: ["Slippery, sharp reef", "Incoming tide can strand you on the reef", "No lifeguard tower"],
      waterQuality: "Check advisories after rain.",
      lifeguard: "No dedicated lifeguard tower — nearest towers at Tamarack.",
    },
    activities: [
      { kind: "tidepool", rating: "great", note: "Low tide reef" },
      { kind: "surf", rating: "good", note: "Reef point" },
      { kind: "photo", rating: "great" },
      { kind: "swim", rating: "fair", note: "Unguarded, rocky" },
      { kind: "dogs", rating: "avoid" },
    ],
    packing: { bring: ["Water shoes with grip", "Tide chart", "Water and snacks"], rentNearby: ["Food and restrooms at Tamarack/Carlsbad Village"], skip: ["Collecting marine life — it's protected"] },
    photoGuide: { bestTime: "Low tide at golden hour.", bestSpots: ["Reef shelves with tide-pool reflections", "Bluff path looking north"], tips: ["A polarizer cuts glare on pools."] },
    seasonalCalendar: { summary: "Winter and spring minus tides are prime tide-pool season; reef surf works best in winter.", rows: { crowd: CROWD_QUIET, water: WATER, surf: SURF_WINTER, weather: SUN_NORTH, wildlife: [3, 3, 3, 2, 1, 1, 1, 1, 1, 2, 3, 3] } },
    bestFor: [{ label: "Tide pools", icon: "Fish" }, { label: "Reef surf", icon: "Waves" }, { label: "Photography", icon: "Camera" }],
    parkingSnapshot: { cost: "Free", bestLot: "Carlsbad Blvd shoulder", backupLot: "Cerezo Drive area", fillsBy: "Mid-morning weekends" },
    dining: [
      { slug: "campfire", reason: "Wood-fired dinner in the Village", distance: "6-min drive" },
      { slug: "pizza-port", reason: "Pizza and craft beer", distance: "6-min drive" },
    ],
    nearbyAttractions: [
      { to: "/beaches/warm-water-jetty", label: "Warm Water Jetty", hint: "Surf wedge just north." },
      { to: "/beaches/tamarack", label: "Tamarack", hint: "Restrooms and lifeguards." },
      { to: "/guides/tide-pooling-calendar", label: "Tide-pool calendar", hint: "Find this month's minus tides." },
    ],
  },
  {
    id: "warm-water-jetty",
    name: "Warm Water Jetty",
    location: "Carlsbad",
    imageKey: "warm-water-jetty",
    maps: maps("Warm Water Jetty, Carlsbad, CA"),
    coords: { lat: 33.1385, lng: -117.3440 },
    paragraphs: [
      "Warm Water Jetty sits at the outlet of Agua Hedionda Lagoon, just south of Tamarack. It earned its name from the old Encina power plant, which discharged warm water here; the plant shut down in 2018, so the water today is the same temperature as the rest of the coast — but the name stuck.",
      "The jetty bends incoming swell into a punchy wedge that draws shortboarders and bodyboarders, especially on west and northwest swells. The sandy beach on either side is good for walks and watching the surf action up close.",
      "Free parking lines Carlsbad Boulevard along the beach. There are no restrooms right at the jetty; the nearest are at Tamarack.",
    ],
    insiderTip: "Watch a few sets before paddling out — the wedge breaks close to the rocks and the locals know the takeoff spots.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "Free along Carlsbad Blvd" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Seasonal tower nearby" },
      { icon: "Bath", label: "RESTROOMS", value: "Nearest at Tamarack" },
      { icon: "Waves", label: "Best for", value: "Wedge surf, bodyboarding" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "easy", restrooms: "limited", lifeguard: "seasonal", dogs: "no", accessibility: "partial",
      notes: { parking: "Free roadside parking", restrooms: "Nearest at Tamarack", lifeguard: "Seasonal state coverage", dogs: "No dogs on state beach" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    gettingThere: {
      driving: "From I-5 exit Tamarack Avenue or Cannon Road west to Carlsbad Boulevard; the jetty is at the Agua Hedionda outlet.",
      parking: "Free parking along Carlsbad Blvd.",
      transit: "Carlsbad Village COASTER station is about 1.5 miles north.",
      tips: ["Mid tide is usually best for the wedge", "Great spot to watch surfing from the sand"],
    },
    safety: {
      rip: "Strong current along the jetty and lagoon outlet.",
      hazards: ["Waves break near the rocks", "Lagoon outflow currents", "Slippery jetty — don't climb it"],
      waterQuality: "Lagoon outflow can affect water quality after rain.",
      lifeguard: "State Parks lifeguards cover the area in season.",
    },
    activities: [
      { kind: "surf", rating: "great", note: "Jetty wedge" },
      { kind: "photo", rating: "great", note: "Surf action" },
      { kind: "fish", rating: "good", note: "Shore fishing (license)" },
      { kind: "swim", rating: "fair", note: "Currents by jetty" },
      { kind: "dogs", rating: "avoid" },
    ],
    packing: { bring: ["Bodyboard and fins", "Wetsuit", "Telephoto lens"], rentNearby: ["Boards at Carlsbad surf shops"], skip: ["Climbing the jetty"] },
    photoGuide: { bestTime: "Afternoon light on the wedge.", bestSpots: ["Sand just north of the jetty", "Carlsbad Blvd overlook"], tips: ["Fast shutter for surfers in the wedge."] },
    seasonalCalendar: { summary: "Fun year-round on the right swell; winter brings the best wedges.", rows: { crowd: CROWD_BUSY, water: WATER, surf: SURF_WINTER, weather: SUN_NORTH } },
    bestFor: [{ label: "Surfing", icon: "Waves" }, { label: "Surf watching", icon: "Camera" }, { label: "Easy parking", icon: "Car" }],
    parkingSnapshot: { cost: "Free", bestLot: "Carlsbad Blvd at the jetty", backupLot: "Tamarack area", fillsBy: "Early on swell days" },
    dining: [
      { slug: "senor-grubby-s", reason: "Burritos and pitchers in the Village", distance: "5-min drive" },
      { slug: "pizza-port", reason: "Pizza and craft beer", distance: "5-min drive" },
    ],
    nearbyAttractions: [
      { to: "/beaches/tamarack", label: "Tamarack", hint: "Restrooms and family beach." },
      { to: "/beaches/terramar-beach", label: "Terramar Beach", hint: "Tide pools just south." },
      { to: "/guides/carlsbad-village", label: "Carlsbad Village", hint: "Shops and dining." },
    ],
  },

  // ─── Encinitas & Leucadia ─────────────────────────────────
  {
    id: "beacons-beach",
    name: "Beacon's Beach",
    location: "Leucadia, Encinitas",
    imageKey: "beacons",
    maps: maps("Beacon's Beach, Encinitas, CA"),
    coords: { lat: 33.0648, lng: -117.3050 },
    paragraphs: [
      "Beacon's is Leucadia's classic surf beach: a narrow strip of sand beneath towering sandstone bluffs, reached by a switchback trail cut into the cliff. Up top, a small free lot on Neptune Avenue offers one of the best sunset overlooks in North County.",
      "Below, reef and sand bottoms create peaks for longboarders and shortboarders, and at low tide the beach widens enough for long walks north toward Grandview. The bluffs are actively eroding — the City of Encinitas periodically repairs or closes the trail, so check access before you go.",
      "There are no restrooms or towers at the bottom; Encinitas lifeguards patrol the coast. Pack in what you need and pack out everything.",
    ],
    insiderTip: "Even if you don't surf, the Neptune Avenue lot at sunset is one of the best free views in San Diego County.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "Small free lot on Neptune Ave" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Patrol only" },
      { icon: "Bath", label: "RESTROOMS", value: "None" },
      { icon: "Waves", label: "Best for", value: "Surf, sunset views, quiet walks" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "hard", restrooms: "no", lifeguard: "limited", dogs: "no", accessibility: "none",
      notes: { parking: "Tiny free lot fills early", restrooms: "None on site", lifeguard: "Encinitas lifeguard patrols", dogs: "No dogs on Encinitas beaches", accessibility: "Steep switchback bluff trail" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    gettingThere: {
      driving: "From I-5 exit Leucadia Boulevard west to Neptune Avenue, then turn north to the Beacon's lot.",
      parking: "Small free lot at the top of the trail; residential street parking on Neptune (respect posted rules).",
      transit: "Encinitas COASTER station is about 2 miles south.",
      tips: ["Check the city site for trail closures", "Wear real shoes for the switchbacks"],
    },
    safety: {
      rip: "Moderate currents around reef sections; unguarded.",
      hazards: LEUCADIA_SAFETY_HAZARDS,
      waterQuality: "Check county advisories after rain.",
      lifeguard: "Encinitas lifeguards patrol; no staffed tower at the beach.",
    },
    activities: [
      { kind: "surf", rating: "great", note: "Reef and sand peaks" },
      { kind: "sunset", rating: "great", note: "Bluff-top lot" },
      { kind: "photo", rating: "great" },
      { kind: "swim", rating: "fair", note: "Unguarded" },
      { kind: "dogs", rating: "avoid" },
    ],
    packing: { bring: ["Sturdy shoes", "Water", "Light bag for the trail"], rentNearby: ["Boards at Leucadia surf shops on Coast Hwy 101"], skip: ["Heavy coolers — it's a steep trail"] },
    photoGuide: { bestTime: "Sunset from the bluff-top lot.", bestSpots: ["Top of the switchbacks", "Beach looking north at low tide"], tips: ["Golden light on the bluffs an hour before sunset."] },
    seasonalCalendar: { summary: "Best surf in fall and winter; summer sunsets draw a crowd to the overlook.", rows: { crowd: CROWD_QUIET, water: WATER, surf: SURF_WINTER, weather: SUN_NORTH } },
    bestFor: [{ label: "Surfing", icon: "Waves" }, { label: "Sunsets", icon: "Sun" }, { label: "Solitude", icon: "Heart" }],
    parkingSnapshot: { cost: "Free", bestLot: "Beacon's lot on Neptune Ave", backupLot: "Neptune Ave / Leucadia Blvd streets", fillsBy: "8 AM on swell days" },
    dining: [
      { slug: "fish-101", reason: "Grilled fish on Coast Hwy 101", distance: "4-min drive" },
      { slug: "juanita-s-taco-shop", reason: "Late-night tacos", distance: "6-min drive" },
    ],
    nearbyAttractions: [
      { to: "/beaches/grandview-beach", label: "Grandview Beach", hint: "Staircase beach just north." },
      { to: "/beaches/stone-steps-beach", label: "Stone Steps Beach", hint: "Quiet stairway beach south." },
      { to: "/beaches/moonlight-beach", label: "Moonlight Beach", hint: "Full facilities in Encinitas." },
    ],
  },
  {
    id: "grandview-beach",
    name: "Grandview Beach",
    location: "Leucadia, Encinitas",
    imageKey: "grandview",
    maps: maps("Grandview Beach, Encinitas, CA"),
    coords: { lat: 33.0763, lng: -117.3111 },
    paragraphs: [
      "Grandview lives up to its name. A long wooden staircase drops down a high bluff from a small free lot at Neptune Avenue and Grandview Street, delivering you to a wide, sandy beach that feels miles from anywhere.",
      "It's a favorite of local families, longboarders and walkers. At low tide you can stroll north toward Ponto or south to Beacon's. The stairs are long — roughly a hundred steps — so pack light.",
      "There's a portable or seasonal restroom at the top in some seasons, but plan for minimal facilities. Encinitas lifeguards cover Grandview seasonally.",
    ],
    insiderTip: "Arrive before 9 AM for a lot spot, or park on Neptune and walk — the stairs are the only way down.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "Small free lot + street" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Seasonal" },
      { icon: "Bath", label: "RESTROOMS", value: "Limited at the top" },
      { icon: "Heart", label: "Best for", value: "Quiet sand, longboarding, walks" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "hard", restrooms: "limited", lifeguard: "seasonal", dogs: "no", accessibility: "none",
      notes: { parking: "Small free lot fills early", restrooms: "Limited facilities at top", lifeguard: "Encinitas, in season", dogs: "No dogs on Encinitas beaches", accessibility: "Long staircase only" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    gettingThere: {
      driving: "From I-5 exit La Costa Avenue west, then go south on Coast Hwy 101 and west on Grandview Street to Neptune Avenue.",
      parking: "Free lot at the top of the stairs; residential street parking nearby.",
      transit: "Encinitas COASTER station is about 3 miles south.",
      tips: ["Pack light for the stairs", "Low tide opens long beach walks"],
    },
    safety: {
      rip: "Moderate rips between sandbars.",
      hazards: LEUCADIA_SAFETY_HAZARDS,
      waterQuality: "Check county advisories after rain.",
      lifeguard: "Encinitas lifeguards staff the beach seasonally.",
    },
    activities: [
      { kind: "surf", rating: "good", note: "Longboard-friendly" },
      { kind: "swim", rating: "good" },
      { kind: "sunset", rating: "great" },
      { kind: "photo", rating: "great", note: "Staircase view" },
      { kind: "dogs", rating: "avoid" },
    ],
    packing: { bring: ["Light beach bag", "Backpack chair", "Water"], rentNearby: ["Leucadia surf shops on 101"], skip: ["Big coolers and wagons"] },
    photoGuide: { bestTime: "Late afternoon light on the staircase and bluffs.", bestSpots: ["Top landing of the stairs", "Waterline looking back at the bluff"], tips: ["Shoot down the staircase for a leading line."] },
    seasonalCalendar: { summary: "Mellow summers and empty fall afternoons; winter brings bigger surf.", rows: { crowd: CROWD_QUIET, water: WATER, surf: SURF_WINTER, weather: SUN_NORTH } },
    bestFor: [{ label: "Quiet beach day", icon: "Heart" }, { label: "Longboarding", icon: "Waves" }, { label: "Photos", icon: "Camera" }],
    parkingSnapshot: { cost: "Free", bestLot: "Grandview lot at Neptune Ave", backupLot: "Neptune Ave street parking", fillsBy: "9 AM weekends" },
    dining: [
      { slug: "fish-101", reason: "Seafood favorite in Leucadia", distance: "4-min drive" },
      { slug: "herb-sea", reason: "Seasonal dinner in Encinitas", distance: "8-min drive" },
    ],
    nearbyAttractions: [
      { to: "/beaches/beacons-beach", label: "Beacon's Beach", hint: "Bluff-trail surf beach to the south." },
      { to: "/beaches/south-ponto-beach", label: "South Ponto Beach", hint: "Lagoon-mouth surf to the north." },
    ],
  },
  {
    id: "stone-steps-beach",
    name: "Stone Steps Beach",
    location: "Encinitas",
    imageKey: "stone-steps",
    maps: maps("Stone Steps Beach, S El Portal St, Encinitas, CA"),
    coords: { lat: 33.0548, lng: -117.2990 },
    paragraphs: [
      "Stone Steps is a hidden-in-plain-sight Encinitas beach reached by a steep concrete staircase at the end of South El Portal Street. It's tucked between Moonlight Beach and Beacon's, and most visitors drive right past it.",
      "At the bottom you'll find a narrow beach with sand, cobbles and reef patches — great for low-tide walks, tide pooling on minus tides, and a quieter surf session than Moonlight's crowds. At high tide the sand can nearly disappear.",
      "There are no facilities, and parking is residential street parking only. Respect neighbors, keep noise down and pack out trash.",
    ],
    insiderTip: "Walk south at low tide and you'll reach Moonlight Beach's restrooms and snack bar in about 15 minutes.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "Residential street only" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Patrol only" },
      { icon: "Bath", label: "RESTROOMS", value: "None (Moonlight is nearest)" },
      { icon: "Fish", label: "Best for", value: "Low-tide walks, quiet surf" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "moderate", restrooms: "no", lifeguard: "limited", dogs: "no", accessibility: "none",
      notes: { parking: "Free residential streets", restrooms: "Nearest at Moonlight Beach", lifeguard: "Encinitas patrols", dogs: "No dogs on Encinitas beaches", accessibility: "Steep stairs only" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    tidePool: { spot: "Reef patches below the stairs", note: "Small pools appear on minus tides; easy walk to Moonlight at low tide.", maxHeight: 0.5, link: { to: "/guides/tide-pooling-calendar", label: "Tide-pooling calendar" } },
    gettingThere: {
      driving: "From I-5 exit Leucadia Boulevard or Encinitas Boulevard, take Coast Hwy 101 to Neptune Avenue, then S El Portal Street.",
      parking: "Free street parking on Neptune Ave and nearby streets.",
      transit: "Encinitas COASTER station is about a mile south.",
      tips: ["Check tide before going — high tide covers most of the beach", "No services"],
    },
    safety: {
      rip: "Currents around reef patches; unguarded.",
      hazards: LEUCADIA_SAFETY_HAZARDS,
      waterQuality: "Check county advisories after rain.",
      lifeguard: "Encinitas lifeguards patrol; nearest tower at Moonlight Beach.",
    },
    activities: [
      { kind: "tidepool", rating: "good", note: "Minus tides" },
      { kind: "surf", rating: "good" },
      { kind: "photo", rating: "good", note: "Staircase" },
      { kind: "swim", rating: "fair", note: "Unguarded, rocky" },
      { kind: "dogs", rating: "avoid" },
    ],
    packing: { bring: ["Water shoes", "Tide chart", "Small bag"], rentNearby: ["Food and restrooms at Moonlight Beach"], skip: ["Big gear — steep stairs"] },
    photoGuide: { bestTime: "Low tide near sunset.", bestSpots: ["Top of the staircase", "Reef at low tide looking north"], tips: ["Steps make a strong leading line."] },
    seasonalCalendar: { summary: "Winter minus tides reveal the reef; summer is quiet compared to Moonlight.", rows: { crowd: CROWD_QUIET, water: WATER, surf: SURF_WINTER, weather: SUN_NORTH } },
    bestFor: [{ label: "Hidden beach", icon: "Heart" }, { label: "Tide pools", icon: "Fish" }, { label: "Low-tide walks", icon: "Sun" }],
    parkingSnapshot: { cost: "Free", bestLot: "S El Portal / Neptune Ave street", backupLot: "Moonlight Beach lot", fillsBy: "Mid-morning summer weekends" },
    dining: [
      { slug: "swami-s-cafe", reason: "Smoothie bowls and breakfast", distance: "4-min drive" },
      { slug: "herb-sea", reason: "Seasonal coastal dinner", distance: "4-min drive" },
    ],
    nearbyAttractions: [
      { to: "/beaches/moonlight-beach", label: "Moonlight Beach", hint: "Restrooms, snack bar, lifeguards." },
      { to: "/beaches/beacons-beach", label: "Beacon's Beach", hint: "Bluff-trail surf beach north." },
    ],
  },

  // ─── Solana Beach ─────────────────────────────────────────
  {
    id: "tide-park-beach",
    name: "Tide Park Beach Access",
    location: "Solana Beach",
    imageKey: "tide-park",
    maps: maps("Tide Beach Park, Solana Beach, CA"),
    coords: { lat: 32.9980, lng: -117.2775 },
    paragraphs: [
      "Tide Park (Tide Beach Park) is Solana Beach's tide-pool access point: a small bluff-top park with a staircase leading down to a wide rocky reef. At low tide the reef drains into a maze of pools full of anemones, sea stars, crabs and hermit crabs.",
      "At higher tides the beach narrows and the reef breaks waves for local surfers. Families visit on minus tides; photographers come for the moody reef at sunset.",
      "Parking is limited street parking along Pacific Avenue and Solana Vista Drive. There are no restrooms or lifeguard towers at Tide Park — Fletcher Cove, a short drive south, has both.",
    ],
    insiderTip: "Go on a minus tide (below 0 ft) in winter or spring and arrive 30 minutes before the low — the reef is at its best right around the bottom of the tide.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "Limited street parking" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Patrol only" },
      { icon: "Bath", label: "RESTROOMS", value: "None (Fletcher Cove nearest)" },
      { icon: "Fish", label: "Best for", value: "Tide pools, reef photos" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "hard", restrooms: "no", lifeguard: "limited", dogs: "no", accessibility: "none",
      notes: { parking: "Few street spots on Pacific Ave", restrooms: "Nearest at Fletcher Cove", lifeguard: "Solana Beach patrols", dogs: "No dogs on Solana Beach beaches", accessibility: "Staircase to the reef" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    tidePool: { spot: "Tide Park reef", note: "Wide reef with abundant pools on minus tides; touch gently, take nothing.", maxHeight: 0.5, link: { to: "/guides/tide-pooling-calendar", label: "Tide-pooling calendar" } },
    gettingThere: {
      driving: "From I-5 exit Lomas Santa Fe Drive west, then north on Highway 101 and west on Solana Vista Drive to Pacific Avenue.",
      parking: "Limited free street parking; observe posted residential rules.",
      transit: "Solana Beach COASTER/Amtrak station is about a mile away.",
      tips: ["Check the tide chart — this beach is all about low tide", "Wear grippy water shoes"],
    },
    safety: {
      rip: "Currents around the reef; unguarded.",
      hazards: ["Slippery algae on the reef", "Incoming tide can cut off return routes", "Unstable bluffs — stay away from the base of the cliff"],
      waterQuality: "Check advisories after rain.",
      lifeguard: "Solana Beach lifeguards patrol; towers at Fletcher Cove.",
    },
    activities: [
      { kind: "tidepool", rating: "great" },
      { kind: "photo", rating: "great" },
      { kind: "surf", rating: "good", note: "Reef at mid tide" },
      { kind: "swim", rating: "fair", note: "Rocky, unguarded" },
      { kind: "dogs", rating: "avoid" },
    ],
    packing: { bring: ["Water shoes", "Tide chart", "Small towel"], rentNearby: ["Snacks on Highway 101"], skip: ["Buckets for collecting — marine life is protected"] },
    photoGuide: { bestTime: "Low tide at golden hour or overcast mornings.", bestSpots: ["Bottom of the stairs looking out over the reef", "Pools with the bluff behind"], tips: ["Get low to reflect the sky in pools."] },
    seasonalCalendar: { summary: "Winter and spring bring daytime minus tides; summer lows are often early morning.", rows: { crowd: CROWD_QUIET, water: WATER, weather: SUN_NORTH, wildlife: [3, 3, 3, 2, 1, 1, 1, 1, 1, 2, 3, 3] } },
    bestFor: [{ label: "Tide pools", icon: "Fish" }, { label: "Kids exploring", icon: "Users" }, { label: "Photography", icon: "Camera" }],
    parkingSnapshot: { cost: "Free", bestLot: "Pacific Ave street parking", backupLot: "Fletcher Cove lot", fillsBy: "Fills fast on minus-tide weekends" },
    dining: [
      { slug: "fidel-s-little-mexico", reason: "Local Mexican institution", distance: "4-min drive" },
      { slug: "naked-cafe", reason: "Breakfast and coffee", distance: "4-min drive" },
      { slug: "pillbox-tavern", reason: "Burgers and beers", distance: "4-min drive" },
    ],
    nearbyAttractions: [
      { to: "/beaches/fletcher-cove", label: "Fletcher Cove", hint: "Restrooms and lifeguards to the south." },
      { to: "/guides/tide-pooling-calendar", label: "Tide-pool calendar", hint: "Find the next minus tide." },
    ],
  },
  {
    id: "del-mar-shores-beach",
    name: "Del Mar Shores Beach",
    location: "Solana Beach",
    imageKey: "del-mar-shores",
    maps: maps("Del Mar Shores Beach, Solana Beach, CA"),
    coords: { lat: 32.9720, lng: -117.2690 },
    paragraphs: [
      "Despite the name, Del Mar Shores is in Solana Beach — the southernmost beach access in the city, at the end of Del Mar Shores Terrace. A paved path and stairs descend the bluff to a wide, sandy beach that's popular with families, beach volleyball players and surfers.",
      "The beach links north toward Fletcher Cove and south toward Del Mar's Dog Beach at low tide, so long walks are easy. Del Mar Shores Park at the top has open lawn space.",
      "Parking is a small free lot and street spaces near the access path. Seasonal lifeguards cover the beach in summer.",
    ],
    insiderTip: "Walk south at low tide to the San Dieguito River mouth for great birdwatching and Dog Beach people-watching.",
    quickFacts: [
      { icon: "Car", label: "Parking", value: "Small free lot + street" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Seasonal" },
      { icon: "Bath", label: "RESTROOMS", value: "Limited" },
      { icon: "Users", label: "Best for", value: "Families, long walks, surf" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "moderate", restrooms: "limited", lifeguard: "seasonal", dogs: "no", accessibility: "partial",
      notes: { parking: "Small free lot fills on weekends", restrooms: "Limited facilities", lifeguard: "Solana Beach, summer", dogs: "No dogs on Solana Beach beaches", accessibility: "Paved path partway; stairs to sand" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    gettingThere: {
      driving: "From I-5 exit Via de la Valle west, then north on Highway 101 to Del Mar Shores Terrace.",
      parking: "Small free lot at the end of Del Mar Shores Terrace plus street parking.",
      transit: "Solana Beach COASTER station is about 1.5 miles north.",
      tips: ["Arrive early on summer weekends", "Combine with Dog Beach walk at low tide"],
    },
    safety: {
      rip: "Rips form between sandbars; swim near a staffed tower.",
      hazards: ["Unstable bluffs — stay clear of the cliff base", "High tide narrows the beach"],
      waterQuality: "Check advisories after rain; San Dieguito River mouth is to the south.",
      lifeguard: "Solana Beach lifeguards staff the beach seasonally.",
    },
    activities: [
      { kind: "swim", rating: "good" },
      { kind: "surf", rating: "good" },
      { kind: "volleyball", rating: "good" },
      { kind: "picnic", rating: "good", note: "Park at the top" },
      { kind: "sunset", rating: "great" },
      { kind: "dogs", rating: "avoid" },
    ],
    packing: { bring: ["Beach chairs", "Volleyball", "Sunscreen"], rentNearby: ["Snacks on Highway 101"], skip: ["Dogs (try Del Mar Dog Beach)"] },
    photoGuide: { bestTime: "Sunset with the bluffs glowing.", bestSpots: ["Top of the access path", "Waterline looking north"], tips: ["Low-angle light on the cliffs."] },
    seasonalCalendar: { summary: "Busy summer weekends, peaceful fall and winter walks.", rows: { crowd: CROWD_BUSY, water: WATER, surf: SURF_SOUTH, weather: SUN_NORTH } },
    bestFor: [{ label: "Families", icon: "Users" }, { label: "Long walks", icon: "Sun" }, { label: "Surfing", icon: "Waves" }],
    parkingSnapshot: { cost: "Free", bestLot: "Del Mar Shores Terrace lot", backupLot: "Highway 101 street spaces", fillsBy: "10 AM summer weekends" },
    dining: [
      { slug: "jake-s-del-mar", reason: "Oceanfront dining in Del Mar", distance: "5-min drive" },
      { slug: "pillbox-tavern", reason: "Casual pub in Solana Beach", distance: "5-min drive" },
    ],
    nearbyAttractions: [
      { to: "/beaches/del-mar-dog-beach", label: "Del Mar Dog Beach", hint: "Just south across the river mouth." },
      { to: "/beaches/fletcher-cove", label: "Fletcher Cove", hint: "Solana Beach's main cove." },
    ],
  },

  // ─── Del Mar ──────────────────────────────────────────────
  {
    id: "del-mar-dog-beach",
    name: "Del Mar Dog Beach (North Beach)",
    location: "Del Mar",
    imageKey: "del-mar-dog-beach",
    maps: maps("Del Mar Dog Beach, Del Mar, CA"),
    coords: { lat: 32.9775, lng: -117.2685 },
    paragraphs: [
      "Del Mar's North Beach — known to everyone as Dog Beach — stretches from the San Dieguito River mouth north to the Solana Beach line. It's one of the few San Diego County beaches where dogs can run off leash, and on a weekend morning it's pure joy: retrievers in the surf, puppies chasing each other, and happy owners strolling the wide sand.",
      "Off-leash rules are seasonal: the City of Del Mar allows dogs off leash under voice control from the day after Labor Day through June 15, and requires leashes from June 16 through Labor Day. Always confirm current posted rules at the beach.",
      "Parking is paid in the area, including lots near the river mouth and the Del Mar Fairgrounds side, with metered street parking in Del Mar. Bring bags and fresh water — owners are responsible for cleanup.",
    ],
    insiderTip: "Come at low tide — the beach doubles in width and the river mouth creates calm shallows dogs love to splash in.",
    quickFacts: [
      { icon: "Dog", label: "Dogs", value: "Off-leash day after Labor Day–June 15" },
      { icon: "Car", label: "Parking", value: "Paid lots + meters nearby" },
      { icon: "LifeBuoy", label: "Lifeguards", value: "Seasonal" },
      { icon: "Bath", label: "RESTROOMS", value: "Limited nearby" },
      WATER_TEMP_FACT,
    ],
    amenities: {
      parking: "moderate", restrooms: "limited", lifeguard: "seasonal", dogs: "seasonal", accessibility: "partial",
      notes: { parking: "Paid lots and meters", restrooms: "Limited facilities nearby", lifeguard: "Del Mar, summer", dogs: "Off-leash day after Labor Day–Jun 15; leashed in summer", accessibility: "Flat sand from river-mouth side" },
    },
    tideStationId: NORTH_COUNTY_TIDES,
    gettingThere: {
      driving: "From I-5 exit Via de la Valle west, then south on Camino del Mar to the river mouth area at the north end of Del Mar.",
      parking: "Paid lots near the San Dieguito River mouth and metered street parking in Del Mar.",
      transit: "Solana Beach COASTER station is about 2 miles north.",
      tips: ["Bring water and poop bags", "Check posted leash dates before unclipping"],
    },
    safety: {
      rip: "Currents near the river mouth; keep dogs and kids out of the channel when it's flowing.",
      hazards: ["River-mouth currents after rain", "Lots of excited dogs — keep yours under voice control", "Hot sand in summer for paws"],
      waterQuality: "River outflow can raise bacteria after rain — skip swimming for 72 hours after storms.",
      lifeguard: "Del Mar lifeguards staff towers seasonally.",
    },
    activities: [
      { kind: "dogs", rating: "great", note: "Seasonal off-leash" },
      { kind: "swim", rating: "good" },
      { kind: "surf", rating: "good" },
      { kind: "photo", rating: "great", note: "Happy dogs" },
      { kind: "sunset", rating: "great" },
    ],
    packing: { bring: ["Leash (required in summer)", "Poop bags", "Fresh water and bowl", "Towels for the car"], rentNearby: ["Pet supplies in Del Mar and Solana Beach"], skip: ["Glass", "Aggressive or unvaccinated dogs"] },
    photoGuide: { bestTime: "Morning light with dogs splashing at the waterline.", bestSpots: ["River mouth shallows", "Waterline looking north to the bluffs"], tips: ["Burst mode and fast shutter for running dogs."] },
    seasonalCalendar: { summary: "Fall through spring is off-leash season; summer requires leashes and brings more crowds.", rows: { crowd: CROWD_BUSY, water: WATER, surf: SURF_SOUTH, weather: SUN_NORTH }, events: [{ months: "Sep–Jun", label: "Off-leash season" }, { months: "Jul–Sep", label: "Del Mar racing season nearby" }] },
    bestFor: [{ label: "Dogs", icon: "Dog" }, { label: "Morning walks", icon: "Sun" }, { label: "Photos", icon: "Camera" }],
    parkingSnapshot: { cost: "Paid lots / meters", bestLot: "River-mouth lot", backupLot: "Camino del Mar meters", fillsBy: "9 AM weekends" },
    dining: [
      { slug: "board-brew", reason: "Legendary sandwiches to go", distance: "4-min drive" },
      { slug: "jake-s-del-mar", reason: "Oceanfront dining", distance: "5-min drive" },
      { slug: "zel-s-del-mar", reason: "Pizza and burgers in the village", distance: "5-min drive" },
    ],
    nearbyAttractions: [
      { to: "/beaches/powerhouse-park", label: "Powerhouse Park", hint: "Del Mar's main beach and park." },
      { to: "/guides/ocean-beach-dog-beach-guide", label: "Ocean Beach Dog Beach", hint: "San Diego's year-round off-leash beach." },
      { to: "/beaches/del-mar-shores-beach", label: "Del Mar Shores Beach", hint: "Family beach just north." },
    ],
  },
];

NORTH_COUNTY_BEACHES.forEach((b) => {
  b.photoGallery ??= [{ src: NORTH_COUNTY_IMAGES[b.imageKey], alt: `${b.name} in ${b.location}`, caption: b.name }];
  b.faqs ??= [
    { q: `Where do I park for ${b.name}?`, a: b.gettingThere?.parking ?? "See the parking section above." },
    { q: `Are dogs allowed at ${b.name}?`, a: b.amenities.notes?.dogs ?? (b.amenities.dogs === "no" ? "No, dogs are not permitted on the beach." : "Check posted rules.") },
    { q: `Are there lifeguards at ${b.name}?`, a: b.safety?.lifeguard ?? "Check with local lifeguards." },
    { q: `What's the best time to visit ${b.name}?`, a: b.seasonalCalendar?.summary ?? b.insiderTip },
  ];
});

export const NORTH_COUNTY_AMENITIES: Record<string, BeachAmenities> = Object.fromEntries(
  NORTH_COUNTY_BEACHES.map((b) => [b.id, b.amenities]),
);
